import { createContext, useContext, useEffect, ReactNode } from 'react'
import { useNavigate, NavigateOptions, To } from 'react-router-dom'

import AppService from '../services/app.service'

export type NavigateFunction = (to: To, options?: NavigateOptions) => void

const NavigationContext = createContext<{ navigate: NavigateFunction }>({
  navigate: () => {
    console.warn('Navigation attempted before provider was initialized')
  }
})

export const useNavigation = () => useContext(NavigationContext)

export class NavigationSingleton {
  private static instance: NavigationSingleton
  private _navigate: NavigateFunction | null = null
  private _globalRedirectHandler: ((url: string) => void) | null = null
  private _baseUrl: string = ''

  private constructor() {
    this._baseUrl = AppService.getAppBaseURL() || globalThis.location.origin || ''
  }

  public static getInstance(): NavigationSingleton {
    if (!NavigationSingleton.instance) {
      NavigationSingleton.instance = new NavigationSingleton()
    }
    return NavigationSingleton.instance
  }

  public setNavigate(navigate: NavigateFunction): void {
    this._navigate = navigate
  }

  public setGlobalRedirectHandler(handler: (url: string) => void): void {
    this._globalRedirectHandler = handler
  }

  public setBaseUrl(baseUrl: string): void {
    this._baseUrl = baseUrl
  }

  public navigate(to: To, options?: NavigateOptions): void {
    const toStr = typeof to === 'string' ? to : to.pathname || ''
    const isInternalRoute = this.isInternalRoute(toStr)

    if (isInternalRoute && this._navigate) {
      this._navigate(to, options)
    } 
    else if (this._globalRedirectHandler) {
      const fullUrl = this.ensureFullUrl(toStr)
      this._globalRedirectHandler(fullUrl)
    } 
    else {
      const fullUrl = this.ensureFullUrl(toStr)
      
      if (options?.replace) {
        globalThis.location.replace(fullUrl)
      } else {
        globalThis.location.href = fullUrl
      }
    }
  }

  private isInternalRoute(url: string): boolean {
    if (url.startsWith('/')) {
      return true
    }

    if (this._baseUrl && url.startsWith(this._baseUrl)) {
      return true
    }

    return false
  }

  private ensureFullUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    
    if (url.startsWith('/')) {
      return `${globalThis.location.origin}${url}`
    }

    return `${globalThis.location.origin}${globalThis.location.pathname}/${url}`
  }

  public getPathFromUrl(url: string): string {
    if (url.startsWith('/')) {
      return url
    }

    if (this._baseUrl && url.startsWith(this._baseUrl)) {
      return url.substring(this._baseUrl.length) || '/'
    }
      
    return url
  }
}

export const NavigationService = NavigationSingleton.getInstance()

export const setNavigateFunction = (navigate: NavigateFunction) => {
  NavigationService.setNavigate(navigate)
}

export const resetNavigateFunction = () => {
  NavigationService.setNavigate(() => {
    console.warn('Navigation attempted after provider was unmounted')
  })
}

const NavigationConnector = () => {
  const navigate = useNavigate()
  
  useEffect(() => {
    setNavigateFunction(navigate)
    
    return () => {
      resetNavigateFunction()
    }
  }, [navigate])
  
  return null
}

interface NavigationProviderProps {
  children: ReactNode
}

export const NavigationProvider = ({
    children
  }: NavigationProviderProps) => {
  const navigationHandler: NavigateFunction = (to, options) => {
    NavigationService.navigate(to, options)
  }

  return <NavigationContext.Provider value={{ navigate: navigationHandler }}>
      <NavigationConnector />
      {children}
    </NavigationContext.Provider>
}